import { invariant } from '@epic-web/invariant'
import { type LoaderFunctionArgs } from '@remix-run/node'
import { Await, useLoaderData } from '@remix-run/react'
import { Suspense } from 'react'
import { summariseStatement } from '#app/lib/content/summarise/index.js'
import { getLinesByDayAndRange } from '#app/models.server/lines/index.js'
import { getSectionById } from '#app/models.server/sections/index.js'
import { getSpeechesWithMembersByDayAndSection } from '#app/models.server/speeches/index.js'
import Summary from './Summary/index.js'

export async function loader({ params }: LoaderFunctionArgs) {
  async function getStatementSummary(sectionId: string) {
    const section = await getSectionById(sectionId)
    invariant(section, 'Section not found')

    const lines = await getLinesByDayAndRange(
      section.day,
      section.start,
      section.end,
    )

    const speeches = await getSpeechesWithMembersByDayAndSection(
      section.day,
      section.id,
    )
    const statementSpeech = speeches.find(s => s.memberId)
    invariant(statementSpeech, 'Statement not found')

    return await summariseStatement({
      lines: lines.filter(
        l => l.index > statementSpeech?.start && l.index < statementSpeech?.end,
      ),
    })
  }

  const { sectionId } = params
  invariant(sectionId, 'Missing sectionId')

  const summary = getStatementSummary(sectionId)

  return {
    summary,
  }
}

export const handle = {
  name: 'section',
}

export default function SectionSummary() {
  const { summary } = useLoaderData<typeof loader>()

  return (
    <Suspense fallback={<div>Loading</div>}>
      <Await resolve={summary}>
        {summary => <Summary summary={summary} />}
      </Await>
    </Suspense>
  )
}
